import { ExclamationCircleIcon } from "@heroicons/react/outline";
import classNames from "classnames";
import { useCallback, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams, Link } from "react-router-dom";

import {
  LISBlock,
  LISCase,
  LISCaseList,
  LISSpecimen,
  LISTask,
  Specimen as SpecimenType,
} from "pwapi";

import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { procedureEnumToLabel } from "shared/utils/specimenUtils";

import { getCaseForDocument } from "app/actions/documentActions";
import { clinicalHistoryNotEnteredString } from "app/constants";
import { useAppDispatch, useAppSelector } from "app/hooks";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import useGrossingOnScanCallbacks from "hooks/useGrossingOnScanCallbacks";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import AccessionNumber from "ui/components/AccessionNumber";
import CasePriority from "ui/components/CasePriority";
import ClinicName from "ui/components/ClinicName";
import DotAdminLink from "ui/components/DotAdminLink";
import SpecimenProtocolTags from "ui/components/SpecimenProtocolTags";
import Spinner from "ui/components/Spinner";
import UserFullName from "ui/components/UserFullName";
import CaseInfo from "ui/grossing/CaseInfo";
import CheckSVG from "ui/grossing/CheckSVG";
import MidcheckSVG from "ui/grossing/MidcheckSVG";
import UncheckSVG from "ui/grossing/UncheckSVG";
import { labelForBlock } from "util/blockUtils";
import { compareCaseToModMed } from "util/caseUtils";
import objectFromBarcode, {
  BarcodePayload,
  mmBarcodeFromSpecimen,
} from "util/objectFromBarcode";
import { specimenSort } from "util/sortFunctions";

import {
  toastErrorBlockDoesNotBelong,
  toastErrorBlocksNotScannedOut,
  toastErrorBlocksNotScannedOutMovingSpecimen,
  toastErrorIncompleteGrossingTasks,
  toastErrorSpecimenDoesNotBelong,
} from "./GrossingToastErrors";

export const CaseAttributes = ({ c }: { c: LISCase }) => {
  return (
    <dl>
      <div>
        <dt>CLIA</dt>
        <dd>Pathology Services Inc.</dd>
      </div>
      <div>
        <dt>Patient</dt>
        <dd>
          {c.patient?.last_name}, {c.patient?.first_name}
        </dd>
      </div>

      <div>
        <dt>Clinic</dt>
        <dd>
          <ClinicName id={c.patient.clinic_id} />
        </dd>
      </div>

      <div>
        <dt>Ordering Provider</dt>
        <dd>
          <UserFullName id={c.ordering_doctor_id} />
        </dd>
      </div>
    </dl>
  );
};

export const Specimen = ({
  specimen,
  check = false,
  onScanSpecimen,
  pathCase,
}: {
  specimen: LISSpecimen;
  check?: boolean;
  onScanSpecimen: Function;
  pathCase: LISCaseList;
}) => {
  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );
  const blocksNotScannedOut = pathCase?.blocks?.filter(
    (b) =>
      b.specimen_id === specimen.id &&
      b.lis_state === LISBlock.lis_state.UNPROCESSED
  );

  return (
    <article
      className="WorkList__item Scannable"
      onClick={(e) => {
        // TODO: this should be abstracted
        if (lisAltClickScanning && e.altKey) {
          onScanSpecimen();
        }
      }}
    >
      <div className="WorkList__item--specimen">
        {check && (
          <figure>
            {specimen.lis_state === "grossed" ? (
              blocksNotScannedOut?.length ? (
                <MidcheckSVG />
              ) : (
                <CheckSVG />
              )
            ) : (
              <UncheckSVG />
            )}
          </figure>
        )}
        <h2 className="Label">
          <strong>{specimen.name}</strong>
        </h2>

        <div className="WorkList__item--specimen__details">
          {specimen.procedure && (
            <p>
              <code>
                {procedureEnumToLabel(
                  specimen.procedure as SpecimenType.procedure
                )}
              </code>
            </p>
          )}
          <p>
            <code>{specimen.site}</code>
          </p>
          {!!specimen.protocols?.length && (
            <div>
              <SpecimenProtocolTags
                specimen={specimen}
                specimens={pathCase.specimens}
              />
            </div>
          )}
        </div>

        {!specimen.clinical_history ? (
          <div className="Tag Tag--yellow">
            {clinicalHistoryNotEnteredString}
          </div>
        ) : (
          <p className="WorkList__item__clinical_history">
            {specimen.clinical_history}
          </p>
        )}
      </div>
      {!!blocksNotScannedOut?.length && (
        <footer className="WorkList__item__warning">
          <ExclamationCircleIcon height={"1.25em"} />
          {blocksNotScannedOut
            .map((b) => labelForBlock(b, pathCase))
            .join(", ")}{" "}
          {blocksNotScannedOut.length > 1 ? "have" : "has"} not been scanned out
        </footer>
      )}
    </article>
  );
};

const GrossingStationDocument = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const lab = useSelectedLab();
  const selectedLabStation = useSelectedLabStation();

  const document_id = Number(useParams().document_id);

  const pathCase = useAppSelector((state) => state.case);
  const document = pathCase?.documents?.find((d) => d.id === document_id);
  const specimens = [...(pathCase?.specimens || [])].sort(specimenSort);

  const { onScanBlock, onScanDocument, onScanSpecimen } =
    useGrossingOnScanCallbacks();

  const allowIncompleteScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "allowIncompleteScanning")
  );

  const onBarcodeScan = useCallback(
    (barcode: BarcodePayload) => {
      if (!pathCase) return;

      toast.dismiss();
      const { object_type, object_id, mod_med_payload } = barcode;
      if (
        pathCase &&
        mod_med_payload &&
        compareCaseToModMed(pathCase, mod_med_payload)
      ) {
        const specimen = pathCase?.specimens.find(
          (s) => s.name === mod_med_payload?.specimen_name
        );
        if (specimen?.id) {
          const blocksNotScannedOut = pathCase.blocks
            ?.filter((b) => b.specimen_id !== specimen.id)
            .filter((b) => b.lis_state === LISBlock.lis_state.UNPROCESSED);
          if (
            allowIncompleteScanning || // Allow the feature flag to bypass this
            !blocksNotScannedOut?.length || // No other blocks have been "not scanned out"
            blocksNotScannedOut.find((b) => b.specimen_id === specimen.id) // Or the specimen you're trying to access has blocks needing to be scanned out
          ) {
            onScanSpecimen(specimen.id);
          } else {
            toastErrorBlocksNotScannedOut(
              pathCase,
              specimen,
              blocksNotScannedOut
            );
          }
        } else {
          toastErrorSpecimenDoesNotBelong(pathCase!);
        }
      } else if (object_type === "specimen") {
        const specimen = pathCase?.specimens.find((s) => s.id === object_id);
        if (specimen?.id) {
          const blocksNotScannedOut =
            pathCase?.blocks.filter(
              (b) => b.lis_state === LISBlock.lis_state.UNPROCESSED
            ) || [];
          if (
            allowIncompleteScanning || // Allow the feature flag to bypass this
            !blocksNotScannedOut?.length || // No other blocks have been "not scanned out"
            blocksNotScannedOut.find((b) => b.specimen_id === specimen.id) // Or the specimen you're trying to access has blocks needing to be scanned out
          ) {
            onScanSpecimen(specimen.id);
          } else {
            toastErrorBlocksNotScannedOut(
              pathCase,
              specimen,
              blocksNotScannedOut
            );
          }
        } else {
          toastErrorSpecimenDoesNotBelong(pathCase);
        }
      } else if (object_type === "document") {
        const incompleteGrossingTasks = pathCase?.work_processes
          .flatMap((w) => w.tasks)
          .filter((t) => t.type === LISTask.type.GROSSING && !t.completed_at);

        if (object_id === document_id) {
          toast.error(`Already Grossing ${pathCase?.accession}`);
        } else {
          if (allowIncompleteScanning || !incompleteGrossingTasks?.length) {
            onScanDocument(object_id);
          } else {
            toastErrorIncompleteGrossingTasks(
              pathCase,
              incompleteGrossingTasks
            );
          }
        }
      } else if (object_type === "block") {
        const block = pathCase?.blocks.find((b) => b.id === object_id);
        if (block) {
          const blocksNotScannedOut = pathCase?.blocks?.filter(
            (b) => b.lis_state === LISBlock.lis_state.UNPROCESSED
          );
          const specimen = pathCase.specimens?.find(
            (s) => s.id === block.specimen_id
          );
          if (!specimen) {
            toastErrorBlockDoesNotBelong(pathCase);
            return;
          }

          if (
            allowIncompleteScanning ||
            block.lis_state === LISBlock.lis_state.UNPROCESSED ||
            !blocksNotScannedOut?.length
          ) {
            onScanBlock(object_id);
          } else {
            toastErrorBlocksNotScannedOutMovingSpecimen(
              pathCase,
              specimen,
              block,
              blocksNotScannedOut
            );
          }
        } else {
          toastErrorBlockDoesNotBelong(pathCase);
        }
      } else {
        return false;
      }
    },
    [pathCase, onScanSpecimen, onScanBlock]
  );

  useBarcodeScanner(onBarcodeScan);

  const getCaseArgs = {
    parentLookupLabStationId: selectedLabStation.id,
    id: document_id,
  };

  const loadingCase = useIsLoading(getCaseForDocument, getCaseArgs);

  useEffect(() => {
    if (!loadingCase && !document) {
      dispatch(getCaseForDocument(getCaseArgs)).catch((error) => {
        toast.error(error.message);
        navigate("/station/grossing/");
      });
    }
  }, [document_id]);

  if (loadingCase || !document || !pathCase) return <Spinner />;

  return (
    <div
      className={classNames("Station", "Split", {
        "Split--document": lab?.settings?.paperless_grossing?.active,
      })}
    >
      <main>
        <header className="Header">
          <Link to="/station/grossing">
            <figure className="Logo">
              <img src="/logo.svg" alt="PathologyWatch" />
            </figure>
          </Link>
          {pathCase?.priority && (
            <p>
              <CasePriority priorityCase={pathCase} />
            </p>
          )}
          <p>
            <AccessionNumber accession={pathCase?.accession} />
          </p>
          <p className="Subtitle">Scan Bottle to Continue</p>
          {pathCase?.id && <DotAdminLink caseId={pathCase.id} />}
        </header>

        <div className="WorkList">
          {specimens?.map((s) => (
            <Specimen
              specimen={s}
              pathCase={pathCase}
              key={s.id}
              check={true}
              onScanSpecimen={() => {
                onBarcodeScan(
                  objectFromBarcode(
                    pathCase?.external_order_id
                      ? mmBarcodeFromSpecimen(pathCase, s)
                      : `specimen-${s.id}`
                  )
                );
              }}
            />
          ))}
        </div>
      </main>
      <aside>{!!pathCase && <CaseInfo c={pathCase} />}</aside>
    </div>
  );
};
export default GrossingStationDocument;
